import img from "../assests/images/South-Travels-Logo.png";
import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Percent,
  Plane,
  Hotel,
  PhoneCall,
  Mail,
} from "lucide-react";

export default function Header({ onLoginClick, user, setUser }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const dropdownRef = useRef();
  const mobileMenuRef = useRef();

  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close user dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      // Close mobile menu
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navLinkClass = ({ isActive }) =>
    `px-3 py-2 rounded-lg md:rounded-full text-sm md:text-xs lg:text-sm font-medium transition ${isActive
      ? "bg-teal-100 text-teal-700"
      : "text-gray-700 hover:bg-teal-50 hover:text-teal-600"
    }`;

  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  const navItems = (
    <>
      <NavLink to="/" className={navLinkClass} onClick={handleMobileNavClick}>
        Home
      </NavLink>

      <NavLink to="/hotels" className={navLinkClass} onClick={handleMobileNavClick}>
        Hotels
      </NavLink>

      <NavLink to="/about-us" className={navLinkClass} onClick={handleMobileNavClick}>
        About Us
      </NavLink>

      <NavLink to="/services" className={navLinkClass} onClick={handleMobileNavClick}>
        Services
      </NavLink>

      <NavLink to="/contact-us" className={navLinkClass} onClick={handleMobileNavClick}>
        Contact Us
      </NavLink>

      <NavLink to="/blogs" className={navLinkClass} onClick={handleMobileNavClick}>
        Blogs
      </NavLink>
    </>
  );





  return (
    <>
      {/* Top Bar (unchanged) */}
      <div className="bg-gradient-to-r from-teal-400 to-cyan-500 text-white text-xs overflow-hidden">
        <div className="max-w-screen-2xl mx-auto px-4 py-3 flex">
          <div className="flex whitespace-nowrap animate-[marquee_20s_linear_infinite] gap-12">
            <span className="flex items-center gap-2 font-medium">
              <Percent size={15} />
              Summer Sale: <span className="font-semibold">Save up to 60%</span> on Dubai Hotels
            </span>

            <span className="flex items-center gap-2">
              <Plane size={15} />
              Best Flight Deals Available Today
            </span>

            <span className="flex items-center gap-2">
              <Hotel size={15} />
              Free Cancellation on Selected Hotels
            </span>

            <a href="tel:+971501234567" className="flex items-center gap-2 hover:text-white/80 transition">
              <PhoneCall size={15} />
              +971 50 123 4567
            </a>

            <a href="mailto:info@southtravels.com" className="flex items-center gap-2 hover:text-white/80 transition">
              <Mail size={15} />
              info@southtravels.com
            </a>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-md border-b">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-4 py-3">
          {/* Logo */}
          <NavLink to="/" onClick={() => window.scrollTo(0, 0)}>
            <img
              src={img}
              alt="Logo"
              className="h-[40px] sm:h-[50px] xl:h-[70px] w-auto"
            />
          </NavLink>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2 lg:gap-4 bg-gradient-to-r from-teal-400 to-cyan-500 px-3 lg:px-4 py-2 rounded-full">
            {navItems}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Wishlist */}
            <NavLink
              to="/wishlist"
              className="hidden lg:flex items-center gap-2 bg-pink-50 text-pink-600 px-3 py-2 rounded-full text-sm"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              Wishlist
            </NavLink>

            {/* User */}
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-full"
                >
                  <svg
                    className="w-4 h-4 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>

                  {user.name.split(" ")[0]}

                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-lg z-50">
                    <NavLink
                      to="/profile"
                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100"
                    >
                      Profile
                    </NavLink>

                    <NavLink
                      to="/wishlist"
                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100"
                    >
                      Wishlist
                    </NavLink>

                    <button
                      onClick={() => {
                        localStorage.removeItem("user");
                        setUser(null);
                        setDropdownOpen(false);
                        navigate("/"); // 👈 redirect home
                      }}
                      className="flex items-center gap-2 w-full text-left px-4 py-2 text-red-500 hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={onLoginClick}
                className="hidden sm:flex items-center gap-2 bg-teal-500 text-white px-4 py-2 rounded-full"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 12H3m12 0l-4-4m4 4l-4 4m6-8v12"
                  />
                </svg>
                Sign In
              </button>
            )}

            {/* Mobile Menu Wrapper */}
            <div className="relative md:hidden" ref={mobileMenuRef}>
              {/* Hamburger Button */}
              <button
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>

              {/* Mobile Menu */}
              <div
                className={`fixed top-0 left-0 w-full h-auto max-h-screen overflow-y-auto bg-white z-50 transition-all duration-300 ${mobileMenuOpen
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-full opacity-0"
                  }`}
              >
                <div className="p-6 space-y-6">

                  {/* Top bar (Close button) */}
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold">Menu</h2>

                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-gray-600 text-2xl"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Nav Items */}
                  <div className="flex flex-col gap-2">{navItems}</div>

                  {/* Wishlist */}
                  <NavLink
                    to="/wishlist"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-pink-50 text-pink-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Wishlist
                  </NavLink>

                  {/* Sign In */}
                  {!user && (
                    <button
                      onClick={() => {
                        onLoginClick();
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 bg-teal-500 text-white rounded-lg"
                    >
                      Sign In
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}